import { fetchSqlDataWithQuery, insertData } from '../services/api';

const normalizeSourcePath = (p: any): string =>
  String(p ?? '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .trim();

const basename = (p: string): string => {
  const parts = normalizeSourcePath(p).split('/').filter(Boolean);
  return parts[parts.length - 1] || p;
};

const escapeSqlLiteral = (v: string): string => String(v).replace(/'/g, "''");

const unwrapRows = <T,>(resp: any): T[] => {
  if (Array.isArray(resp)) return resp as T[];
  if (Array.isArray(resp?.data)) return resp.data as T[];
  if (Array.isArray(resp?.data?.data)) return resp.data.data as T[];
  return [];
};

export class AlreadyQueuedError extends Error {
  paths: string[];

  constructor(paths: string[]) {
    const names = paths.map(basename);
    const sample = names.slice(0, 5).join(', ');
    const more = names.length > 5 ? ` (+${names.length - 5} more)` : '';
    super(`File already queued for translation and cannot be queued again: ${sample}${more}`);
    this.name = 'AlreadyQueuedError';
    this.paths = paths;
  }
}

export type TranslationLogQueueRow = {
  transmittal_no: string;
  document_no: string;
  revision?: string;
  folder_path: string;
  source_file_path: string;
  status?: string;
  requested_by?: string | null;
};

const findAlreadyQueuedPaths = async (paths: string[]): Promise<string[]> => {
  const unique = Array.from(new Set(paths.map(normalizeSourcePath).filter(Boolean)));
  if (!unique.length) return [];

  const inList = unique.map((p) => `'${escapeSqlLiteral(p)}'`).join(', ');
  const q = `SELECT source_file_path FROM dcc_translation_log WHERE source_file_path IN (${inList})`;
  const resp = await fetchSqlDataWithQuery(q);
  const rows = unwrapRows<{ source_file_path?: string }>(resp);
  return rows
    .map((r) => normalizeSourcePath(r?.source_file_path))
    .filter(Boolean);
};

/**
 * Inserts new translation-log rows only.
 * If any source_file_path already exists in dcc_translation_log, throws and changes nothing.
 */
export async function queueNewTranslationLogRows(rows: TranslationLogQueueRow[]): Promise<number> {
  const cleaned = (rows || [])
    .map((r) => ({
      transmittal_no: String(r.transmittal_no ?? ''),
      document_no: String(r.document_no ?? ''),
      revision: String(r.revision ?? ''),
      folder_path: normalizeSourcePath(r.folder_path),
      source_file_path: normalizeSourcePath(r.source_file_path),
      status: String(r.status || 'queued'),
      requested_by: r.requested_by ?? null,
    }))
    .filter((r) => r.source_file_path);

  if (!cleaned.length) {
    throw new Error('No valid source_file_path values to queue.');
  }

  const seen = new Set<string>();
  const batchDupes: string[] = [];
  for (const r of cleaned) {
    const key = r.source_file_path.toLowerCase();
    if (seen.has(key)) batchDupes.push(r.source_file_path);
    else seen.add(key);
  }
  if (batchDupes.length) {
    const names = Array.from(new Set(batchDupes.map(basename)));
    throw new Error(`Duplicate source file(s) in this queue request: ${names.slice(0, 5).join(', ')}`);
  }

  const already = await findAlreadyQueuedPaths(cleaned.map((r) => r.source_file_path));
  if (already.length) {
    throw new AlreadyQueuedError(already);
  }

  try {
    await insertData(
      'dcc_translation_log',
      cleaned.map((r) => ({
        transmittal_no: r.transmittal_no,
        document_no: r.document_no,
        revision: r.revision,
        folder_path: r.folder_path,
        source_file_path: r.source_file_path,
        status: r.status,
        requested_by: r.requested_by,
      })),
    );
  } catch (e: any) {
    const msg = String(e?.response?.data?.error || e?.message || '');
    if (/unique|duplicate|source_file_path/i.test(msg)) {
      throw new AlreadyQueuedError(cleaned.map((r) => r.source_file_path));
    }
    throw e;
  }

  return cleaned.length;
}
